/* eslint-disable react/jsx-filename-extension */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import T from 'i18n-react';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import Router from 'next/router';
import qs from 'qs';

import MetaTags from 'Components/MetaTags/MetaTags';
import NotFound404 from 'Components/NotFound404/NotFound404';
import { OtpSetupModalContainer } from 'Components/OtpSetupModal/OtpSetupModalContainer';
import PageComposer from 'Components/PageComposer/PageComposer';
import texts from 'Components/texts.json';
import { sanitiseVehicleListQuery } from 'Components/VehicleList/sanitiseVehicleList';
import { getMetaTitleFromQuery, isPageValid } from 'Components/VehicleList/VehicleList.helpers';
import VehicleListWrapper from 'Components/VehicleList/VehicleListWrapper';
import { logger } from 'Services/logger/logger';
import { useFilters, useResetFilters } from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import { useMakes } from 'Stores/MakesStore/MakesStore';
import { PAGE_SLUGS } from 'Utilities/consts';
import fetchSavedSearchesWithExceptionLogger from 'Utilities/fetchers/fetchSavedSearches';
import { getInitialFilters } from 'Utilities/helpers/filters.helpers';
import isClientSideAuth from 'Utilities/helpers/isClientSideAuth';
import useSetSavedFilters from 'Utilities/hooks/useSetSavedFilters';
import proPropTypes from 'Utilities/proPropTypes';
import ROUTES, { API_ROUTES } from 'Utilities/routes';

import { routeToHomepageIfUserBlockedFromAPI } from '../utilities/helpers/blockedDealers.helpers';

T.setTexts(texts);

const setPromotionBannerVisibility = (
	<script
		dangerouslySetInnerHTML={{
			__html: `
(function() {
  try {
    if (localStorage.getItem('mobilePromotionsBanner') === 'true') {
      document.documentElement.classList.add('banner-dismissed');
    } 
  } catch (e) {

  }
})();
`,
		}}
	/>
);

const VehicleListPage = ({ query, savedFilters }) => {
	const makes = useMakes();
	const filters = useFilters();
	const resetFilters = useResetFilters();

	const is404 = !isPageValid({ makes, query });
	const metaTitle = getMetaTitleFromQuery({ makes, query });
	const metaDescription = T.translate('metaTags.pages.vehicleList.description');

	useSetSavedFilters(savedFilters);

	useEffect(() => {
		if (isEmpty(query)) {
			resetFilters();
		}
	}, [resetFilters, query]);

	if (is404) {
		return <NotFound404 pageSlug={PAGE_SLUGS.VEHICLE_LIST} />;
	}

	return (
		<>
			<OtpSetupModalContainer origin="otp_setup_on_login" />
			<MetaTags noIndex customTags={setPromotionBannerVisibility} description={metaDescription} title={metaTitle} />
			<PageComposer pageSlug={PAGE_SLUGS.VEHICLE_LIST}>
				<VehicleListWrapper filters={filters} query={query} />
			</PageComposer>
		</>
	);
};

VehicleListPage.getInitialProps = async (context) => {
	const { asPath, isSSR, makes, models, query } = context;
	const redirectQueries = qs.stringify({ redirect: asPath, unauthorised: true });
	const redirectUrl = `${ROUTES.SIGN_IN.href}?${redirectQueries}`;
	const sanitisedQuery = sanitiseVehicleListQuery(query);
	const is404 = !isPageValid({ makes, query: sanitisedQuery });

	if (isSSR) {
		if (!context.req.user) {
			return context.res.redirect(307, redirectUrl);
		}
	}

	const isNotAuthorizedInClientSide = !isSSR && !isClientSideAuth();
	if (isNotAuthorizedInClientSide) {
		return Router.push(redirectUrl);
	}

	if (!isSSR) {
		routeToHomepageIfUserBlockedFromAPI(API_ROUTES.CHECK_PERMISSIONS).catch(({ message }) =>
			logger.warn({ message, scope: 'routeToHomepageIfUserBlockedFromAPI' }),
		);
	}

	if (is404 && isSSR) {
		context.res.status(404);
	}

	const savedFilters = await fetchSavedSearchesWithExceptionLogger(context);
	const { listType, ...queryExcludingListType } = sanitisedQuery;
	const modelsWithTotalCounts = models?.map((model) => ({ ...model, total: 0 }));
	const initialFilters = getInitialFilters(sanitisedQuery);

	return {
		filters: initialFilters,
		is404,
		isSSR,
		makes: sortBy(makes, ['slug']),
		models: modelsWithTotalCounts,
		query: queryExcludingListType,
		savedFilters,
	};
};

VehicleListPage.defaultProps = {
	featureFlags: {},
	isSSR: false,
	query: {},
	title: '',
};

VehicleListPage.propTypes = {
	is404: PropTypes.bool.isRequired,
	isSSR: PropTypes.bool,
	query: proPropTypes.vehicleFiltersQuery,
	savedFilters: proPropTypes.savedFiltersResponse,
	title: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export default VehicleListPage;
